/**
 * Itinerary date defaulting lives here.
 * Do not compute itinerary `new Date(leadStartDate)` inline anywhere in the app.
 * Never fall back to `new Date()`.
 *
 * Single source of truth for the default `Date` seed used by every itinerary
 * date picker (Add, Custom, Edit, Export to Itinerary, etc.). Centralising
 * this prevents auth, data-fetch, or form-state refactors from silently
 * reintroducing a "today" fallback.
 */
import { useMemo } from "react";

/**
 * Returns a stable `Date` parsed from the lead's start date, or `undefined`
 * when the input is missing/invalid. Memoised on the raw string so the
 * returned reference is stable across renders.
 *
 * Contract:
 * - Input:  string | null | undefined (ISO `yyyy-MM-dd` or full ISO string)
 * - Output: Date | undefined
 * - Never returns "now". Never calls `new Date()` with no argument.
 */
export function useDatePickerDefault(
  leadStartDate: string | null | undefined,
): Date | undefined {
  return useMemo(() => {
    if (typeof leadStartDate !== "string") return undefined;
    const trimmed = leadStartDate.trim();
    if (!trimmed) return undefined;

    // Date-only DB values (`yyyy-MM-dd`) must be parsed as calendar dates,
    // not UTC instants, otherwise users west of UTC see the previous day.
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      if (Number.isNaN(parsed.getTime())) return undefined;
      return parsed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed;
  }, [leadStartDate]);
}
